import { createApi } from '@reduxjs/toolkit/query/react';
import baseQuery from '../../Shared/API/config';

export interface AssignmentReference {
  id?: string | number;
  displayName?: string;
  friendlyName?: string;
  [key: string]: unknown;
}

export interface AssignmentProgress {
  total: number;
  entered: number;
  outstanding: number;
  completionPercent: number;
}

export interface AssignmentPerformance {
  averageScore: number;
  averagePercent: number;
  minScore: number;
  maxScore: number;
}

export interface AssignmentActions {
  canEdit: boolean;
  canDelete: boolean;
  deleteMode?: 'normal' | 'force' | 'blocked';
  requiresReason?: boolean;
  deleteBlockReason?: string | null;
}

export interface Assignment {
  id: string;
  title: string;
  description?: string;
  score: number | '';
  dueDate: string;
  subject: AssignmentReference;
  classroom: AssignmentReference;
  session?: AssignmentReference & { displayName?: string };
  examination?: AssignmentReference & { friendlyName?: string };
  examinationId?: string | number | null;
  type: string;
  progress?: AssignmentProgress;
  performance?: AssignmentPerformance;
  actions: AssignmentActions;
}

export interface DeleteAssignmentRequest {
  id: string;
  reason?: string;
}

export interface AssignmentType {
  id: string;
  display_name: string;
  prefix: string;
  score: number;
}

export interface AssignmentsResponse {
  data: Assignment[];
  meta?: {
    types?: Record<string, string>;
  };
}

interface AssignmentsQueryParams {
  classId?: string | number;
  subjectId?: string | number;
  search?: string;
  sessionId?: string | number;
  examId?: string | number;
}

interface ClassAssignmentsQueryParams {
  classId?: string | number;
  subjectId?: string | number;
  examinationId?: string | number;
}

export const AssignmentsApi = createApi({
  reducerPath: 'AssessmentsApi',
  baseQuery,
  tagTypes: ['assignments', 'classAssignments'],
  endpoints: builder => ({
    getAssignments: builder.query<AssignmentsResponse, AssignmentsQueryParams>({
      query: ({ classId, subjectId, search, sessionId, examId }) => {
        let url = '/academics/assignments';
        const params = new URLSearchParams();
        if (classId) {
          params.append('classId', String(classId));
        }
        if (subjectId) {
          params.append('subjectId', String(subjectId));
        }
        if (search) {
          params.append('q', search);
        }
        if (sessionId) {
          params.append('sessionId', String(sessionId));
        }
        if (examId) {
          params.append('examinationId', String(examId));
        }
        if (params.toString()) {
          url += `?${params.toString()}`;
        }
        return url;
      },
      providesTags: ['assignments'],
    }),
    getAssignment: builder.query<Assignment, string>({
      query: id => `/academics/assignments/${id}`,
      transformResponse: (r: any) => r?.data?.[0] ?? null,
      providesTags: ['assignments'],
    }),
    getClassAssignments: builder.query<
      AssignmentsResponse,
      ClassAssignmentsQueryParams
    >({
      query: ({ classId, subjectId, examinationId }) => {
        const params = new URLSearchParams();
        if (subjectId) {
          params.append('subjectId', String(subjectId));
        }
        if (examinationId) {
          params.append('examinationId', String(examinationId));
        }
        return `/academics/classes/${classId}/assignments?${params.toString()}`;
      },
      providesTags: ['classAssignments'],
    }),
    createAssignment: builder.mutation<unknown, Record<string, unknown>>({
      query: assignment => ({
        url: '/academics/assignments',
        method: 'POST',
        body: assignment,
      }),
      invalidatesTags: ['assignments'],
    }),
    updateAssignment: builder.mutation<
      unknown,
      { id: string } & Record<string, unknown>
    >({
      query: assignment => ({
        url: `/academics/assignments/${assignment.id}`,
        method: 'PATCH',
        body: assignment,
      }),
      invalidatesTags: ['assignments'],
    }),
    deleteAssignment: builder.mutation<unknown, DeleteAssignmentRequest>({
      query: ({ id, reason }) => ({
        url: `/academics/assignments/${id}`,
        method: 'DELETE',
        body: reason ? { reason } : undefined,
      }),
      invalidatesTags: ['assignments'],
    }),
    getAssignmentTypes: builder.query<
      { data: AssignmentType[] },
      { classId?: string | number }
    >({
      query: ({ classId }) => `/academics/classes/${classId}/assignments/types`,
      providesTags: ['assignments'],
    }),
  }),
});

export const {
  useGetAssignmentsQuery,
  useGetAssignmentQuery,
  useGetClassAssignmentsQuery,
  useCreateAssignmentMutation,
  useUpdateAssignmentMutation,
  useDeleteAssignmentMutation,
  useGetAssignmentTypesQuery,
} = AssignmentsApi;
